'use client'

import { useAuthStore } from '@/lib/stores/auth-store'
import { type LoginType } from '@/lib/auth'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { DEFAULT_ROUTE } from "@/lib/constants";

const resolvedEnvLoginType = resolveEnvLoginType()

function resolveEnvLoginType(): LoginType | null {
  const envValue = process.env.NEXT_PUBLIC_LOGIN_TYPE?.trim().toLowerCase()
  if (envValue === 'remote') {
    return 'remote'
  }
  if (envValue === 'local') {
    return 'local'
  }
  return null
}

export function useAuth() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const {
    isAuthenticated,
    isLoading,
    login,
    logout,
    setToken,
    checkAuth,
    checkAuthRequired,
    error,
    hasHydrated,
    authRequired,
    loginType: storedLoginType,
    setLoginType
  } = useAuthStore()

  useEffect(() => {
    // Only check auth after the store has hydrated from localStorage
    if (hasHydrated) {
      // First check if auth is required
      if (authRequired === null) {
        checkAuthRequired().then((required) => {
          // If auth is required, check if we have valid credentials
          if (required) {
            checkAuth()
          }
        })
      } else if (authRequired) {
        // Auth is required, check credentials
        checkAuth()
      }
      // If authRequired === false, we're already authenticated (set in checkAuthRequired)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated, authRequired])

  useEffect(() => {
    if (resolvedEnvLoginType && resolvedEnvLoginType !== storedLoginType) {
      setLoginType(resolvedEnvLoginType)
    }
  }, [setLoginType, storedLoginType])

  const activeLoginType = resolvedEnvLoginType ?? storedLoginType

  const handleLogin = async (password: string) => {
    // 登录前清除所有缓存数据，确保切换租户后不会显示旧数据
    queryClient.clear()

    let success = false
    if (activeLoginType === 'remote') {
      success = await setToken(password) // 远程登录直接保存token
    } else {
      success = await login(password)
    }

    if (success) {
      // Check if there's a stored redirect path
      const redirectPath = sessionStorage.getItem('redirectAfterLogin')
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin')
        router.push(redirectPath)
      } else {
        router.push(DEFAULT_ROUTE)
      }
    }
    return success
  }

  const handleLogout = () => {
    logout()
    // 登出时清除所有缓存数据
    queryClient.clear()
  }

  return {
    isAuthenticated,
    isLoading: isLoading || !hasHydrated, // Treat lack of hydration as loading
    error,
    login: handleLogin,
    logout: handleLogout
  }
}
